import { useCallback, useMemo, useSyncExternalStore } from "react";
import userStorage, { getCurrentUserId } from "@/lib/user-storage";
import { onStorageWrite } from "@/deployment/standaloneStorage";
import type { MemberTrainingPlan } from "@/lib/memberTrainingDay";

/** The account cache is the shared source, including writes from another browser tab. */
export function useMemberPlanState() {
  const owner = getCurrentUserId();
  const subscribe = useCallback(
    (notify: () => void) =>
      onStorageWrite((key) => {
        if (key === `u_${owner}:member_training_plan`) notify();
      }),
    [owner],
  );
  const snapshot = useCallback(
    () =>
      getCurrentUserId() === owner
        ? userStorage.getItem("member_training_plan")
        : null,
    [owner],
  );
  const raw = useSyncExternalStore(subscribe, snapshot, () => null);
  const plan = useMemo<MemberTrainingPlan | null>(() => {
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [raw]);
  const setPlan = useCallback(
    (
      update:
        | MemberTrainingPlan
        | null
        | ((previous: MemberTrainingPlan | null) => MemberTrainingPlan | null),
    ) => {
      if (getCurrentUserId() !== owner) return;
      const current = snapshot();
      let previous: MemberTrainingPlan | null = null;
      try {
        previous = current ? JSON.parse(current) : null;
      } catch {
        /* Corrupt cache can be replaced. */
      }
      const next = typeof update === "function" ? update(previous) : update;
      if (next)
        userStorage.setItem("member_training_plan", JSON.stringify(next));
      else userStorage.removeItem("member_training_plan");
    },
    [owner, snapshot],
  );
  return [plan, setPlan] as const;
}
